import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/uk";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { AppMonthField } from "../components/form";
import { Cake, Cross, Palmtree } from "lucide-react";
import { SCHEDULE_GROUP_PRESETS, TIMEZONE } from "../constans";
import { isMarkedShift } from "../helpers/scheduleWorkingDaysLookup";
import { isDirectScheduleGroup } from "../helpers/directManagerDialogConversion";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("uk");

const WEEK_UA = ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const GROUP_BG = ["#fff3cd", "#e2d5f1", "#f8d7da", "#d1e7dd", "#cfe2ff", "#e9ecef"];

/** Цикл по кліку: вихідний → зміна → відпустка → лікарняний → вихідний */
const CELL_CYCLE = ["off", "shift", "vacation", "sick"];
/** Дірект: день можуть ділити двоє менеджерів — повний день → 1 зміна → 2 зміна. */
const CELL_CYCLE_DIRECT = ["off", "shift", "shift1", "shift2", "vacation", "sick"];

/** Цифра в клітинці для половинних змін. */
const SHIFT_PART_MARK = { shift1: "1", shift2: "2" };

const CELL_LABELS = {
    off: "вихідний",
    shift: "зміна (повний день)",
    shift1: "1 зміна",
    shift2: "2 зміна",
    vacation: "відпустка",
    sick: "лікарняний",
};

/** Не пропонуємо як шаблон групи (службова мітка архівних рядків). */
const SCHEDULE_ARCHIVE_GROUP = "Архів графіку";

function dayCellState(map, day) {
    const v = map?.[String(day)];
    if (v === true || v === "shift") return "shift";
    if (v === "shift1" || v === "shift2") return v;
    if (v === "vacation") return "vacation";
    if (v === "sick") return "sick";
    return "off";
}

function ymNow() {
    return dayjs().format("YYYY-MM");
}

function monthTitleUk(ym) {
    const t = dayjs(`${ym}-01`);
    const m = t.format("MMMM");
    return (m.charAt(0).toUpperCase() + m.slice(1) + " " + t.format("YYYY")).replace("р.", "").trim();
}

function shiftsCount(map) {
    if (!map) return 0;
    return Object.keys(map).filter((k) => isMarkedShift(map[k])).length;
}

/**
 * Графік змін: клік по клітинці циклічно: вихідний → зміна → відпустка → лікарняний
 * (у групі «Дірект» після зміни ще «1 зміна» і «2 зміна»).
 * Сторінка в меню лише для admin і director; API дозволяє перегляд усім, хто в списку учасників.
 */
export default function WorkSchedulePage({ user }) {
    const [ym, setYm] = useState(ymNow);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [participants, setParticipants] = useState([]);
    const [shifts, setShifts] = useState({});
    const [meta, setMeta] = useState({
        canEditAll: false,
        currentUserId: null,
        canEditDayMarks: true,
        scheduleMonthClosed: false,
    });

    const [showAdd, setShowAdd] = useState(false);
    const [showEdit, setShowEdit] = useState(null);
    const [directory, setDirectory] = useState([]);
    const [addForm, setAddForm] = useState({
        userId: "",
        group: "",
        sortOrder: 0,
    });
    const [saving, setSaving] = useState(false);
    /** @type {{ periodLabel?: string; people?: { displayName: string; segments: { yearMonth: string; monthLocative: string; rangesText: string; daysCount: number }[] }[] } | null} */
    const [vacationsAhead, setVacationsAhead] = useState(null);
    const [vacationsAheadLoading, setVacationsAheadLoading] = useState(false);
    const scheduleScrollRef = useRef(null);
    /** Послідовні PATCH по клітинках — без паралельних запитів (менше 401/гонок після refresh токена). */
    const scheduleCellPatchChain = useRef(Promise.resolve());

    useEffect(() => {
        scheduleCellPatchChain.current = Promise.resolve();
    }, [ym]);

    /** Номер дня (1–31), якщо переглядаємо поточний місяць за TIMEZONE; інакше null. */
    const todayDayInYm = useMemo(() => {
        const now = dayjs().tz(TIMEZONE);
        return now.format("YYYY-MM") === ym ? now.date() : null;
    }, [ym]);

    const loadMonth = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const { data } = await axios.get(`/api/schedule/month?ym=${encodeURIComponent(ym)}`);
            setDaysInMonth(data.daysInMonth || 31);
            setParticipants(data.participants || []);
            setShifts(data.shifts || {});
            setMeta(data.meta || {});
        } catch (e) {
            setErr(e.response?.data?.error || e.message || "Помилка завантаження");
        } finally {
            setLoading(false);
        }
    }, [ym]);

    useEffect(() => {
        loadMonth();
    }, [loadMonth]);

    const fetchVacationsAhead = useCallback(async () => {
        if (user?.role !== "admin") return;
        setVacationsAheadLoading(true);
        try {
            const { data } = await axios.get("/api/schedule/vacations-ahead");
            setVacationsAhead(data && typeof data === "object" ? data : { people: [] });
        } catch {
            setVacationsAhead({ people: [], periodLabel: "" });
        } finally {
            setVacationsAheadLoading(false);
        }
    }, [user?.role]);

    useEffect(() => {
        if (user?.role === "admin") void fetchVacationsAhead();
    }, [user?.role, fetchVacationsAhead]);

    useEffect(() => {
        if (loading || todayDayInYm == null) return;
        if (typeof window !== "undefined" && !window.matchMedia("(min-width: 992px)").matches) return;
        const wrap = scheduleScrollRef.current;
        if (!wrap) return;
        const th = wrap.querySelector(`thead th[data-schedule-col-day="${todayDayInYm}"]`);
        th?.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
    }, [loading, ym, todayDayInYm]);

    /** Мобільний веб: у кожній картці горизонтальний скрол днів — вирівнюємо «сьогодні» по центру. */
    const scrollMobileDayStripsToToday = useCallback(
        (behavior) => {
            if (todayDayInYm == null) return;
            if (typeof window === "undefined" || window.matchMedia("(min-width: 992px)").matches) return;
            const root = document.querySelector(".work-schedule-mobile-list");
            if (!root) return;
            root.querySelectorAll(".work-schedule-mobile-days-scroll").forEach((strip) => {
                const el = strip.querySelector(`[data-schedule-mobile-day="${todayDayInYm}"]`);
                el?.scrollIntoView({ inline: "center", behavior: behavior ?? "smooth", block: "nearest" });
            });
        },
        [todayDayInYm]
    );

    useEffect(() => {
        if (loading) return;
        let cancelled = false;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) scrollMobileDayStripsToToday("smooth");
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(id);
        };
    }, [loading, ym, scrollMobileDayStripsToToday]);

    useEffect(() => {
        if (typeof window === "undefined" || window.matchMedia("(min-width: 992px)").matches) return;
        const onResize = () => scrollMobileDayStripsToToday("auto");
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [scrollMobileDayStripsToToday]);

    const openAdd = async () => {
        setAddForm({ userId: "", group: "", sortOrder: 0 });
        setShowAdd(true);
        try {
            const { data } = await axios.get("/api/users/directory");
            setDirectory(Array.isArray(data) ? data : []);
        } catch {
            setDirectory([]);
        }
    };

    const participantUserIds = useMemo(
        () =>
            new Set(
                participants.filter((p) => !p.historicalScheduleRow).map((p) => String(p.userId))
            ),
        [participants]
    );

    const directoryOptions = useMemo(
        () =>
            directory.filter((u) => u._id && !participantUserIds.has(String(u._id))),
        [directory, participantUserIds]
    );

    const submitAdd = async () => {
        if (!addForm.userId) return;
        setSaving(true);
        try {
            await axios.post("/api/schedule/participants", {
                userId: addForm.userId,
                group: addForm.group,
                sortOrder: addForm.sortOrder,
            });
            setShowAdd(false);
            await loadMonth();
            if (user?.role === "admin") void fetchVacationsAhead();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    const submitEdit = async () => {
        if (!showEdit?.userId) return;
        setSaving(true);
        try {
            await axios.patch(`/api/schedule/participants/${showEdit.userId}`, {
                group: showEdit.group,
                sortOrder: showEdit.sortOrder,
            });
            setShowEdit(null);
            await loadMonth();
            if (user?.role === "admin") void fetchVacationsAhead();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    const removeParticipant = async (userId) => {
        if (
            !window.confirm(
                "Прибрати з поточного списку графіку? Відмітки за минулі місяці збережуться (рядок «Архів графіку»), у нових місяцях без цієї людини."
            )
        )
            return;
        try {
            await axios.delete(`/api/schedule/participants/${userId}`);
            await loadMonth();
            if (user?.role === "admin") void fetchVacationsAhead();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        }
    };

    /** Знову додати в ScheduleParticipant (архівний рядок зникає, дані за місяці лишаються). */
    const restoreParticipantFromArchive = async (userId) => {
        if (
            !window.confirm(
                "Повернути в поточний список графіку? Групу можна змінити кнопкою «Редагувати» (✏️)."
            )
        )
            return;
        setSaving(true);
        try {
            await axios.post("/api/schedule/participants", {
                userId,
                group: "",
                sortOrder: 0,
            });
            await loadMonth();
            if (user?.role === "admin") void fetchVacationsAhead();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    const rowCanEdit = (userId) =>
        meta.canEditAll || String(meta.currentUserId) === String(userId);

    const canToggleDayCell = (userId, historicalScheduleRow) =>
        !historicalScheduleRow && rowCanEdit(userId) && meta.canEditDayMarks;

    const toggleCell = async (userId, day, historicalScheduleRow, group) => {
        if (!canToggleDayCell(userId, historicalScheduleRow)) return;
        const uid = String(userId);
        const d = String(day);
        const cur = dayCellState(shifts[uid], day);
        const cycle = isDirectScheduleGroup(group) ? CELL_CYCLE_DIRECT : CELL_CYCLE;
        /** Половинна зміна поза Діректом (група змінилась) — далі йде відпустка, як після звичайної зміни. */
        const idx = cycle.indexOf(cycle.includes(cur) ? cur : "shift");
        const next = cycle[(idx + 1) % cycle.length];

        setShifts((prevMaps) => {
            const m = { ...(prevMaps[uid] || {}) };
            if (next === "off") delete m[d];
            else m[d] = next;
            return { ...prevMaps, [uid]: m };
        });

        const patchPromise = scheduleCellPatchChain.current.then(() =>
            axios.patch("/api/schedule/cell", {
                userId,
                yearMonth: ym,
                day,
                state: next,
            })
        );
        scheduleCellPatchChain.current = patchPromise.catch(() => {});

        try {
            await patchPromise;
            if (user?.role === "admin") void fetchVacationsAhead();
        } catch (e) {
            await loadMonth();
            alert(e.response?.data?.error || e.message);
        }
    };

    const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

    const isSunday = (d) => dayjs(`${ym}-${String(d).padStart(2, "0")}`).day() === 0;

    const canManage = user?.role === "admin" || user?.role === "director";

    /** Унікальні групи з поточного завантаженого графіку — для автодоповнення в модалках. */
    const existingScheduleGroups = useMemo(() => {
        const s = new Set(SCHEDULE_GROUP_PRESETS);
        for (const p of participants) {
            const g = String(p.group || "").trim();
            if (g && g !== SCHEDULE_ARCHIVE_GROUP) s.add(g);
        }
        return [...s].sort((a, b) => a.localeCompare(b, "uk"));
    }, [participants]);

    /** Рядки зведення відпусток; ім'я тут — для сумісності зі старим JSX. */
    const vacationSummaryRows = useMemo(() => {
        const people = vacationsAhead?.people;
        if (!Array.isArray(people)) return [];
        return people.map((p) => ({
            displayName: p.displayName,
            line: p.segments
                .map((s) => `в ${s.monthLocative} ${s.rangesText} (${s.daysCount}д)`)
                .join(", "),
        }));
    }, [vacationsAhead]);

    const groupedRows = useMemo(() => {
        const rows = [];
        let prevGroup = null;
        let gIdx = -1;
        for (const p of participants) {
            const g = p.group || "";
            if (g !== prevGroup) {
                gIdx += 1;
                prevGroup = g;
                if (g) {
                    rows.push({ type: "group", key: `g-${g}-${gIdx}`, label: g, bg: GROUP_BG[gIdx % GROUP_BG.length] });
                }
            }
            rows.push({ type: "person", key: String(p.userId), ...p });
        }
        return rows;
    }, [participants]);

    /**
     * Колонка «сьогодні»: одна горизонтальна лінія між сусідніми tr з людьми (без подвійного inset).
     * Рядок групи між людьми розриває «стовпчик» — знову повна рамка зверху/знизу сегмента.
     */
    const todayColumnStackByPersonKey = useMemo(() => {
        const m = new Map();
        if (todayDayInYm == null) return m;
        groupedRows.forEach((row, i) => {
            if (row.type !== "person") return;
            const prevPerson = i > 0 && groupedRows[i - 1].type === "person";
            const nextPerson = i < groupedRows.length - 1 && groupedRows[i + 1].type === "person";
            if (!prevPerson && !nextPerson) m.set(row.key, "single");
            else if (!prevPerson && nextPerson) m.set(row.key, "top");
            else if (prevPerson && nextPerson) m.set(row.key, "mid");
            else m.set(row.key, "bottom");
        });
        return m;
    }, [groupedRows, todayDayInYm]);

    return (
        <div className="container-fluid py-3 work-schedule">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h1 className="h4 mb-0 me-auto">Графік змін</h1>
                <div className="d-flex align-items-center gap-1">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setYm(dayjs(`${ym}-01`).subtract(1, "month").format("YYYY-MM"))}
                    >
                        ←
                    </Button>
                    <div className="work-schedule-ym-picker">
                        <AppMonthField value={ym} onChange={setYm} size="sm" />
                    </div>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setYm(dayjs(`${ym}-01`).add(1, "month").format("YYYY-MM"))}
                    >
                        →
                    </Button>
                </div>
                {canManage && (
                    <Button variant="primary" size="sm" onClick={openAdd} aria-label="Додати рядок" title="Додати рядок">
                        {"+ "}
                    </Button>
                )}
            </div>

            {err && <div className="alert alert-danger py-2">{err}</div>}

            {!loading && meta.scheduleMonthClosed && user?.role !== "admin" && (
                <div className="alert alert-secondary py-2 mb-2">
                    Цей місяць уже завершено: змінювати відмітки по днях може лише адміністратор. Перегляд доступний
                    як і раніше.
                </div>
            )}

            <div className="d-none d-lg-block work-schedule-scroll border rounded" ref={scheduleScrollRef}>
                {loading ? (
                    <div className="p-5 text-center">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : (
                    <Table bordered size="sm" className="mb-0 work-schedule-table">
                        <thead className="table-light">
                            <tr>
                                <th className="work-schedule-sticky-name text-nowrap" colSpan={1} rowSpan={2}>
                                    Співробітник
                                </th>
                                <th
                                    className="text-center text-nowrap work-schedule-sticky-sum"
                                    rowSpan={2}
                                    style={{ minWidth: 48 }}
                                    title="Лише зміни (без відпустки та лікарняного)"
                                >
                                    Σ
                                </th>
                                <th className="text-center p-0" colSpan={daysInMonth}>
                                    {monthTitleUk(ym)}
                                </th>
                                {canManage && (
                                    <th className="text-center text-nowrap" rowSpan={2} style={{ width: 88 }}>
                                        Дії
                                    </th>
                                )}
                            </tr>
                            <tr>
                                {days.map((d) => {
                                    const dow = dayjs(`${ym}-${String(d).padStart(2, "0")}`).day();
                                    const isToday = todayDayInYm === d;
                                    return (
                                        <th
                                            key={d}
                                            data-schedule-col-day={d}
                                            title={isToday ? "Сьогодні" : undefined}
                                            className={`text-center p-1 small ${
                                                dow === 0 ? "work-schedule-sun-head" : ""
                                            } ${isToday ? "work-schedule-today-head" : ""}`}
                                            style={{ width: 46, minWidth: 46 }}
                                        >
                                            <div className="work-schedule-weekday-abbr">{WEEK_UA[dow]}</div>
                                            <div className="fw-semibold">{d}</div>
                                            {isToday && (
                                                <div className="work-schedule-today-badge">сьогодні</div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {groupedRows.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={2 + daysInMonth + (canManage ? 1 : 0)}
                                        className="text-center text-muted py-4 work-schedule-empty-hint"
                                    >
                                        Список порожній. Додайте співробітників кнопкою «+» у шапці (адмін / директор).
                                    </td>
                                </tr>
                            )}
                            {groupedRows.map((row) => {
                                if (row.type === "group") {
                                    const colSpan = 2 + daysInMonth + (canManage ? 1 : 0);
                                    return (
                                        <tr key={row.key} className="work-schedule-group-row">
                                            <td
                                                colSpan={colSpan}
                                                className="fw-semibold small py-1"
                                                style={{ background: row.bg }}
                                            >
                                                {row.label}
                                            </td>
                                        </tr>
                                    );
                                }
                                const uid = String(row.userId);
                                const cnt = shiftsCount(shifts[uid]);
                                const editable = canToggleDayCell(row.userId, row.historicalScheduleRow);

                                return (
                                    <tr key={row.key}>
                                        <td className="work-schedule-sticky-name small align-middle">
                                            {row.displayName}
                                        </td>
                                        <td className="text-center small align-middle fw-semibold work-schedule-sticky-sum">
                                            {cnt}
                                        </td>
                                        {days.map((d) => {
                                            const st = dayCellState(shifts[uid], d);
                                            const sun = isSunday(d);
                                            const empty = st === "off";
                                            const isToday = todayDayInYm === d;
                                            const isBday =
                                                typeof row.birthdayDayInMonth === "number" &&
                                                row.birthdayDayInMonth === d;
                                            const tip = isBday
                                                ? `День ${d}: день народження (${CELL_LABELS[st]})${isToday ? " (сьогодні)" : ""}`
                                                : `День ${d}: ${CELL_LABELS[st]}${isToday ? " (сьогодні)" : ""}`;
                                            const a11y = isBday
                                                ? `День ${d}, день народження, ${CELL_LABELS[st]}${isToday ? ", сьогодні" : ""}`
                                                : `День ${d}, ${CELL_LABELS[st]}${isToday ? ", сьогодні" : ""}`;
                                            const todayStackPos =
                                                isToday && todayDayInYm != null
                                                    ? todayColumnStackByPersonKey.get(row.key)
                                                    : null;
                                            const todayStackClass =
                                                todayStackPos && todayStackPos !== "single"
                                                    ? `work-schedule-cell--today-stack-${todayStackPos}`
                                                    : "";
                                            return (
                                                <td
                                                    key={d}
                                                    data-schedule-col-day={d}
                                                    className={`p-0 align-middle text-center ${
                                                        isToday ? "work-schedule-today-col" : ""
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        className={[
                                                            "work-schedule-cell",
                                                            sun && empty ? "work-schedule-cell--sun" : "",
                                                            isMarkedShift(st) ? "work-schedule-cell--work" : "",
                                                            st === "vacation" ? "work-schedule-cell--vacation" : "",
                                                            st === "sick" ? "work-schedule-cell--sick" : "",
                                                            isBday ? "work-schedule-cell--birthday" : "",
                                                            isToday ? "work-schedule-cell--today" : "",
                                                            todayStackClass,
                                                            !editable ? "work-schedule-cell--disabled" : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                        onClick={() => toggleCell(row.userId, d, row.historicalScheduleRow, row.group)}
                                                        disabled={!editable}
                                                        title={tip}
                                                        aria-label={a11y}
                                                    >
                                                        {isBday ? (
                                                            <Cake
                                                                className="work-schedule-cell-icon work-schedule-cell-icon--bday"
                                                                size={17}
                                                                strokeWidth={2.25}
                                                                aria-hidden
                                                            />
                                                        ) : (
                                                            <>
                                                                {SHIFT_PART_MARK[st] && (
                                                                    <span className="work-schedule-cell-shift-part" aria-hidden>
                                                                        {SHIFT_PART_MARK[st]}
                                                                    </span>
                                                                )}
                                                                {st === "vacation" && (
                                                                    <Palmtree
                                                                        className="work-schedule-cell-icon"
                                                                        size={17}
                                                                        strokeWidth={2.25}
                                                                        aria-hidden
                                                                    />
                                                                )}
                                                                {st === "sick" && (
                                                                    <Cross
                                                                        className="work-schedule-cell-icon"
                                                                        size={16}
                                                                        strokeWidth={2.5}
                                                                        aria-hidden
                                                                    />
                                                                )}
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                        {canManage && (
                                            <td className="text-center text-nowrap p-1 align-middle">
                                                {!row.historicalScheduleRow ? (
                                                    <>
                                                        <Button
                                                            variant="link"
                                                            className="p-0 px-1 text-secondary text-decoration-none"
                                                            size="sm"
                                                            onClick={() =>
                                                                setShowEdit({
                                                                    userId: row.userId,
                                                                    group: row.group || "",
                                                                    sortOrder: row.sortOrder ?? 0,
                                                                    displayName: row.displayName,
                                                                })
                                                            }
                                                        >
                                                            ✏️
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            className="p-0 px-1 text-danger text-decoration-none"
                                                            size="sm"
                                                            onClick={() => removeParticipant(row.userId)}
                                                        >
                                                            🗑
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="py-0 px-1 small text-nowrap"
                                                        disabled={saving}
                                                        title="Повернути в поточний список графіку"
                                                        onClick={() => restoreParticipantFromArchive(row.userId)}
                                                    >
                                                        Повернути
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </div>

            <div className="d-lg-none work-schedule-mobile-list">
                {loading ? (
                    <div className="p-5 text-center border rounded">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : (
                    <>
                        {groupedRows.length === 0 && (
                            <div className="text-center text-muted py-5 px-2 border rounded work-schedule-empty-hint">
                                Список порожній. Додайте співробітників кнопкою «+» у шапці (адмін / директор).
                            </div>
                        )}
                        {groupedRows.map((row) => {
                            if (row.type === "group") {
                                return (
                                    <div
                                        key={row.key}
                                        className="work-schedule-mobile-group fw-semibold small px-3 py-2 rounded-3 mb-2"
                                        style={{ background: row.bg }}
                                    >
                                        {row.label}
                                    </div>
                                );
                            }
                            const uid = String(row.userId);
                            const cnt = shiftsCount(shifts[uid]);
                            const editable = canToggleDayCell(row.userId, row.historicalScheduleRow);
                            return (
                                <div
                                    key={row.key}
                                    className="work-schedule-mobile-card border rounded-3 mb-3 p-3 bg-body"
                                >
                                    <div className="d-flex align-items-start gap-2 mb-2">
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="fw-semibold">{row.displayName}</div>
                                        </div>
                                        <div
                                            className="text-center flex-shrink-0 px-1"
                                            style={{ minWidth: 40 }}
                                            title="Лише зміни (без відпустки та лікарняного)"
                                        >
                                            <div className="small text-muted">Σ</div>
                                            <div className="fw-bold">{cnt}</div>
                                        </div>
                                        {canManage && !row.historicalScheduleRow ? (
                                            <div className="d-flex gap-0 flex-shrink-0">
                                                <Button
                                                    variant="link"
                                                    className="p-1 text-secondary text-decoration-none"
                                                    size="sm"
                                                    onClick={() =>
                                                        setShowEdit({
                                                            userId: row.userId,
                                                            group: row.group || "",
                                                            sortOrder: row.sortOrder ?? 0,
                                                            displayName: row.displayName,
                                                        })
                                                    }
                                                >
                                                    ✏️
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="p-1 text-danger text-decoration-none"
                                                    size="sm"
                                                    onClick={() => removeParticipant(row.userId)}
                                                >
                                                    🗑
                                                </Button>
                                            </div>
                                        ) : canManage && row.historicalScheduleRow ? (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="flex-shrink-0"
                                                disabled={saving}
                                                onClick={() => restoreParticipantFromArchive(row.userId)}
                                            >
                                                Повернути
                                            </Button>
                                        ) : null}
                                    </div>
                                    <div className="work-schedule-mobile-days-scroll">
                                        <div className="d-flex flex-nowrap gap-1 pb-1">
                                            {days.map((d) => {
                                                const st = dayCellState(shifts[uid], d);
                                                const sun = isSunday(d);
                                                const empty = st === "off";
                                                const isToday = todayDayInYm === d;
                                                const dow = dayjs(`${ym}-${String(d).padStart(2, "0")}`).day();
                                                const isBday =
                                                    typeof row.birthdayDayInMonth === "number" &&
                                                    row.birthdayDayInMonth === d;
                                                const tip = isBday
                                                    ? `День ${d}: день народження (${CELL_LABELS[st]})${isToday ? " (сьогодні)" : ""}`
                                                    : `День ${d}: ${CELL_LABELS[st]}${isToday ? " (сьогодні)" : ""}`;
                                                return (
                                                    <div
                                                        key={d}
                                                        className="work-schedule-mobile-day-col"
                                                        data-schedule-mobile-day={d}
                                                    >
                                                        <div
                                                            className={`work-schedule-mobile-day-label text-center ${
                                                                dow === 0 ? "work-schedule-mobile-day-label--sun" : ""
                                                            }`}
                                                        >
                                                            {WEEK_UA[dow]}
                                                        </div>
                                                        <div
                                                            className={`work-schedule-mobile-day-num text-center ${
                                                                isToday ? "work-schedule-mobile-day-num--today" : ""
                                                            }`}
                                                        >
                                                            {d}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={[
                                                                "work-schedule-cell",
                                                                "work-schedule-cell--touch",
                                                                sun && empty ? "work-schedule-cell--sun" : "",
                                                                isMarkedShift(st) ? "work-schedule-cell--work" : "",
                                                                st === "vacation" ? "work-schedule-cell--vacation" : "",
                                                                st === "sick" ? "work-schedule-cell--sick" : "",
                                                                isBday ? "work-schedule-cell--birthday" : "",
                                                                isToday ? "work-schedule-cell--today" : "",
                                                                !editable ? "work-schedule-cell--disabled" : "",
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" ")}
                                                            onClick={() =>
                                                                toggleCell(row.userId, d, row.historicalScheduleRow, row.group)
                                                            }
                                                            disabled={!editable}
                                                            title={tip}
                                                            aria-label={tip}
                                                        >
                                                            {isBday ? (
                                                                <Cake
                                                                    className="work-schedule-cell-icon work-schedule-cell-icon--bday"
                                                                    size={18}
                                                                    strokeWidth={2.25}
                                                                    aria-hidden
                                                                />
                                                            ) : (
                                                                <>
                                                                    {SHIFT_PART_MARK[st] && (
                                                                        <span className="work-schedule-cell-shift-part" aria-hidden>
                                                                            {SHIFT_PART_MARK[st]}
                                                                        </span>
                                                                    )}
                                                                    {st === "vacation" && (
                                                                        <Palmtree
                                                                            className="work-schedule-cell-icon"
                                                                            size={18}
                                                                            strokeWidth={2.25}
                                                                            aria-hidden
                                                                        />
                                                                    )}
                                                                    {st === "sick" && (
                                                                        <Cross
                                                                            className="work-schedule-cell-icon"
                                                                            size={17}
                                                                            strokeWidth={2.5}
                                                                            aria-hidden
                                                                        />
                                                                    )}
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>

            <div className="work-schedule-legend small mt-2 text-muted">
                <p className="mb-2 d-lg-none">
                    Торкніться дня: <strong>вихідний</strong> → <strong>зміна</strong> →{" "}
                    <strong>відпустка</strong> → <strong>лікарняний</strong>.
                </p>
                <span className="me-3 d-none d-lg-inline">
                    Клік по дню: <strong>вихідний</strong> → <strong>зміна</strong> →{" "}
                    <strong>відпустка</strong> → <strong>лікарняний</strong> → знову вихідний.
                </span>
                <span className="d-inline-flex flex-wrap align-items-center gap-3">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--shift"
                            role="presentation"
                        />
                        зміна (повний день)
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--shift work-schedule-legend-swatch--part"
                            role="presentation"
                        >
                            1
                        </span>
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--shift work-schedule-legend-swatch--part"
                            role="presentation"
                        >
                            2
                        </span>
                        1 / 2 зміна
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--vacation"
                            role="presentation"
                        />
                        <Palmtree className="work-schedule-legend-icon" size={14} strokeWidth={2.25} aria-hidden />
                        відпустка
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--sick"
                            role="presentation"
                        />
                        <Cross className="work-schedule-legend-icon" size={13} strokeWidth={2.5} aria-hidden />
                        лікарняний
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <span
                            className="work-schedule-legend-swatch work-schedule-legend-swatch--sun"
                            role="presentation"
                        />
                        неділя (порожня клітинка)
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <Cake className="work-schedule-legend-icon" size={14} strokeWidth={2.25} aria-hidden />
                        торт + м’яке рожеве підсвічування (тінь) — день народження рядка
                    </span>
                </span>
            </div>

            {user?.role === "admin" &&
                (vacationsAheadLoading || vacationSummaryRows.length > 0) && (
                    <div className="card border mb-3 mt-3 work-schedule-vacation-summary">
                        <div className="card-header py-2 px-3 small">
                            <div className="fw-semibold">
                                Відпустки
                            </div>
                            {vacationsAhead?.periodLabel ? (
                                <div className="text-muted fw-normal mt-1">{vacationsAhead.periodLabel}</div>
                            ) : null}
                        </div>
                        <div className="card-body py-2 px-3">
                            {vacationsAheadLoading && vacationSummaryRows.length === 0 ? (
                                <div className="text-center py-2">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <ul className="list-unstyled mb-0 small work-schedule-vacation-summary-list">
                                    {vacationSummaryRows.map((r) => (
                                        <li key={r.displayName} className="mb-2">
                                            <span className="fw-medium">{r.displayName}</span> {r.line}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

            <datalist id="work-schedule-group-datalist">
                {existingScheduleGroups.map((g) => (
                    <option key={g} value={g} />
                ))}
            </datalist>

            <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Додати в графік</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2">
                        <Form.Label>Користувач</Form.Label>
                        <Form.Select
                            value={addForm.userId}
                            onChange={(e) => setAddForm((f) => ({ ...f, userId: e.target.value }))}
                        >
                            <option value="">Оберіть…</option>
                            {directoryOptions.map((u) => (
                                <option key={u._id} value={u._id}>
                                    {u.directory?.displayTitle ||
                                        [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(" ") ||
                                        u.username}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Група / локація</Form.Label>
                        <Form.Control
                            list="work-schedule-group-datalist"
                            autoComplete="off"
                            value={addForm.group}
                            onChange={(e) => setAddForm((f) => ({ ...f, group: e.target.value }))}
                            placeholder="Оберіть з підказок або введіть нову"
                        />
                        <Form.Text className="text-muted">
                            Підказки з уже доданих груп; можна ввести будь-який новий текст.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Порядок сортування</Form.Label>
                        <Form.Control
                            type="number"
                            value={addForm.sortOrder}
                            onChange={(e) => setAddForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAdd(false)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" disabled={!addForm.userId || saving} onClick={submitAdd}>
                        {saving ? "…" : "Додати"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!showEdit} onHide={() => setShowEdit(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Рядок графіку</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showEdit && (
                        <>
                            <p className="small text-muted mb-2">{showEdit.displayName}</p>
                            <Form.Group className="mb-2">
                                <Form.Label>Група / локація</Form.Label>
                                <Form.Control
                                    list="work-schedule-group-datalist"
                                    autoComplete="off"
                                    value={showEdit.group}
                                    onChange={(e) => setShowEdit((s) => ({ ...s, group: e.target.value }))}
                                    placeholder="Оберіть з підказок або введіть нову"
                                />
                                <Form.Text className="text-muted">
                                    Підказки з уже доданих груп; можна ввести будь-який новий текст.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Порядок</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={showEdit.sortOrder}
                                    onChange={(e) =>
                                        setShowEdit((s) => ({ ...s, sortOrder: Number(e.target.value) }))
                                    }
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEdit(null)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" disabled={saving} onClick={submitEdit}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
